import * as THREE from "three";
import { Utils } from "../../../utils/utils";
import AppData from "../../../app.data";
import { ArtworkWrapper } from "@/types/artworksData";
import { Slot } from "@/types/slotsData";
import { Loader } from "../../assets/loader";
import { ArtworkLabel } from "../attachments/label.attachment";
import { ArtworkMedia } from "./media.manager";
import { ArtworkFrame } from "../attachments/frame.attachment";
import { ArtworkPedestal } from "../attachments/pedestal.attachment";
import { computeVisibleWorldBoundingSphere, computeVisibleWorldBounds, computeWorldBounds, computeWorldBoundingSphere, createHexagon } from "../../../utils/geometry.utils";
import { resolveArtworkWrapperTransform } from "../../../utils/artworkPlacement.utils";
import { cleanupArtworkRuntimeRegistries } from "../../../utils/runtime-registry.utils";

export class Artwork3dModel {
    private static queueRender(appData: AppData) {
        appData?.renderService?.addToRenderQueue?.();
    }

    private static cleanupStaleModel(utils: Utils, model: any, sceneRoot?: any, extraObjects: any[] = []) {
        cleanupArtworkRuntimeRegistries({ root: model });
        extraObjects.forEach((object) => {
            if (!object) return;
            cleanupArtworkRuntimeRegistries({ root: object });
            utils.disposeOfChildren(object);
        });
        utils.disposeOfChildren(sceneRoot || model);
    }

    private static cleanupOwnedModel(utils: Utils, appData: AppData, model: any, sceneRoot?: any, extraObjects: any[] = []) {
        const ownedRoots = new Set([model, sceneRoot, ...extraObjects].filter(Boolean));
        const isOwned = (object: any) => {
            let current = object;
            while (current) {
                if (ownedRoots.has(current)) return true;
                current = current.parent;
            }
            return false;
        };

        appData.lookAtCameraModels = (appData.lookAtCameraModels || []).filter((object: any) => !isOwned(object));
        appData.emissionModels = (appData.emissionModels || []).filter((object: any) => !isOwned(object));
        this.cleanupStaleModel(utils, model, sceneRoot, extraObjects);
    }

    private static isMoMDioramaSpace(appData: AppData, imageDiorama: string) {
        const spaceType = appData?.galleryData?.spaceType;
        const isPseudoMoM = appData?.spaceData?.subtype === "pseudoMoM";
        return imageDiorama === "diorama" && !isPseudoMoM && (spaceType === "mom" || spaceType === "sellermom");
    }

    private static checkDioramaType(model3D: any) {
        const firstChildName = model3D?.children?.[0]?.name;
        if (firstChildName === "entryPoint") {
            return "artwork";
        }

        for (let index = 0; index < (model3D?.children?.length ?? 0); index++) {
            if (model3D.children[index]?.name === "cap") {
                return "diorama";
            }
        }

        return "3dmodel";
    }

    private static shouldAddPseudoMoMArtworkLabel(appData: AppData, artworkData: ArtworkWrapper) {
        return appData?.spaceData?.subtype === "pseudoMoM" && (artworkData?.artworks?.length || 0) > 0;
    }

    private static shouldShowPseudoMoMArtworkArrows(appData: AppData, artworkData: ArtworkWrapper) {
        return appData?.spaceData?.subtype === "pseudoMoM" && (artworkData?.artworks?.length || 0) > 1;
    }

    private static alignPseudoMoMModelToPillar(model3D: any, targetPosition: any) {
        model3D.updateMatrixWorld?.(true);
        const bounds = computeWorldBounds(model3D);
        const hasFiniteBounds = bounds && [
            bounds.min.x,
            bounds.min.y,
            bounds.min.z,
            bounds.max.x,
            bounds.max.y,
            bounds.max.z,
        ].every(Number.isFinite);

        if (!hasFiniteBounds) {
            return;
        }

        const targetX = Number(targetPosition?.x) || 0;
        const targetY = Number(targetPosition?.y) || 0;
        const targetZ = Number(targetPosition?.z) || 0;
        const centerX = (bounds.min.x + bounds.max.x) / 2;
        const centerZ = (bounds.min.z + bounds.max.z) / 2;

        model3D.position.x += targetX - centerX;
        model3D.position.y += targetY - bounds.min.y + 0.05;
        model3D.position.z += targetZ - centerZ;
    }

    private static createPseudoMoMRotationTarget(model3D: any, savedPivotTransform?: any) {
        model3D.updateMatrixWorld?.(true);
        const bounds = new THREE.Box3().setFromObject(model3D);
        const values = [
            bounds.min.x,
            bounds.min.y,
            bounds.min.z,
            bounds.max.x,
            bounds.max.y,
            bounds.max.z,
        ];
        if (!values.every(Number.isFinite)) {
            return model3D;
        }

        const center = bounds.getCenter(new THREE.Vector3());
        const pivot = new THREE.Group();
        pivot.name = "pseudo-mom-rotation-pivot";
        const savedPivotPosition = savedPivotTransform?.position || savedPivotTransform;
        const hasSavedPivotPosition = savedPivotPosition && [
            savedPivotPosition.x,
            savedPivotPosition.y,
            savedPivotPosition.z,
        ].every(Number.isFinite);

        if (hasSavedPivotPosition) {
            pivot.position.set(savedPivotPosition.x, savedPivotPosition.y, savedPivotPosition.z);
            if (savedPivotTransform?.rotation && [
                savedPivotTransform.rotation.x,
                savedPivotTransform.rotation.y,
                savedPivotTransform.rotation.z,
            ].every(Number.isFinite)) {
                pivot.rotation.set(
                    savedPivotTransform.rotation.x,
                    savedPivotTransform.rotation.y,
                    savedPivotTransform.rotation.z
                );
            }
            if (savedPivotTransform?.scale && [
                savedPivotTransform.scale.x,
                savedPivotTransform.scale.y,
                savedPivotTransform.scale.z,
            ].every(Number.isFinite)) {
                pivot.scale.set(
                    savedPivotTransform.scale.x,
                    savedPivotTransform.scale.y,
                    savedPivotTransform.scale.z
                );
            }
            model3D.position.set(
                model3D.position.x - center.x,
                model3D.position.y - center.y,
                model3D.position.z - center.z
            );
        } else {
            pivot.position.set(center.x, model3D.position.y, center.z);
            model3D.position.x -= pivot.position.x;
            model3D.position.y -= pivot.position.y;
            model3D.position.z -= pivot.position.z;
        }

        pivot.add(model3D);
        return pivot;
    }

    private static renderLabelAsOverlay(sprite: any) {
        sprite.renderOrder = 20;
        sprite.traverse?.((child: any) => {
            child.renderOrder = 20;
            const materials = Array.isArray(child.material)
                ? child.material
                : child.material ? [child.material] : [];
            materials.forEach((material: any) => {
                material.transparent = true;
                material.alphaTest = Math.max(material.alphaTest || 0, 0.01);
                material.depthTest = false;
                material.depthWrite = false;
                material.needsUpdate = true;
            });
        });
    }

    /**
     * Handles setup of 3D model-type artwork in a slot
     * Includes model loading and material configuration
     */
    static async handle3dmodelArtworkSlot(utils: Utils, appData: AppData, artworkData: ArtworkWrapper, loadedMesh: any, slotId: number, loader?: Loader, isLoadCurrent?: () => boolean) {
        let rotationTarget: any = null;
        const cleanupObjectsOnFailure: any[] = [];
        let label: any = null;

        try {
            if (!loadedMesh) {
                console.warn("Artwork3dModel: Missing loaded mesh for 3dmodel slot", {
                    slotId,
                    artworkWrapperId: artworkData?._id
                });
                return { attached: false };
            }

            const slot = utils.getSlotObjectBySlotTarget(slotId, appData)
            if (slot && slot.visible) {
                if (isLoadCurrent && !isLoadCurrent()) {
                    this.cleanupOwnedModel(utils, appData, loadedMesh);
                    return { attached: false, staleCleanupCount: 1 };
                }
                const placement = resolveArtworkWrapperTransform(artworkData, appData.spaceData);
                const usePseudoMoMPivotTransform = appData.spaceData?.subtype === "pseudoMoM" && placement.source === "artwork";
                if (!usePseudoMoMPivotTransform && placement.transform.position) {
                    loadedMesh.position.x = placement.transform.position.x
                    loadedMesh.position.y = placement.transform.position.y
                    loadedMesh.position.z = placement.transform.position.z
                }
                if (!usePseudoMoMPivotTransform && placement.transform.rotation) {
                    loadedMesh.rotation.x = placement.transform.rotation.x
                    loadedMesh.rotation.y = placement.transform.rotation.y
                    loadedMesh.rotation.z = placement.transform.rotation.z
                }

                utils.disposeOfChildren(slot, true)
                utils.scaleMeshToMatchHeight(loadedMesh, artworkData.artworks[artworkData.activeArtwork].artworkData.height * 0.01)
                appData.renderService?.prepareObjectForVR?.(loadedMesh);
                if (appData.spaceData?.subtype === "pseudoMoM" && placement.source !== "artwork") {
                    this.alignPseudoMoMModelToPillar(loadedMesh, placement.transform.position);
                }

                rotationTarget = appData.spaceData?.subtype === "pseudoMoM"
                    ? this.createPseudoMoMRotationTarget(
                        loadedMesh,
                        usePseudoMoMPivotTransform ? placement.transform : undefined
                    )
                    : loadedMesh;

                slot.add(rotationTarget);

                // Add the pedestal to the slot if it does not exist
                const pedestalType = artworkData.artworks[artworkData.activeArtwork].artworkData.pedestal;
                if (pedestalType && pedestalType.type !== "none") {
                    const pedestalMesh = ArtworkPedestal.createPedestal(loadedMesh, artworkData, slot)
                    if (pedestalMesh) {
                        slot.add(pedestalMesh);
                        cleanupObjectsOnFailure.push(pedestalMesh);
                    }
                }

                loadedMesh.traverse((child: any) => {
                    child.userData = { ...slot.userData, artworkWrapper: artworkData } as Slot
                })
                loadedMesh.userData = { ...slot.userData, artworkWrapper: artworkData } as Slot
                if (appData.spaceData?.subtype === "pseudoMoM") {
                    rotationTarget.userData = { ...slot.userData, artworkWrapper: artworkData } as Slot;
                    rotationTarget.userData.pseudoMoMAutoRotate3dModel = true;
                    rotationTarget.userData.pseudoMoMBaseRotationY = rotationTarget.rotation.y;
                    rotationTarget.userData.pseudoMoMAutoRotationOffset = 0;
                }

                if (loader && this.shouldAddPseudoMoMArtworkLabel(appData, artworkData)) {
                    label = await ArtworkLabel.addArtworkLabel(loader, artworkData, {
                        layout: "pseudoMoMFixed",
                        showArrows: this.shouldShowPseudoMoMArtworkArrows(appData, artworkData),
                    });
                    if (isLoadCurrent && !isLoadCurrent()) {
                        this.cleanupOwnedModel(utils, appData, loadedMesh, rotationTarget, [label, ...cleanupObjectsOnFailure]);
                        return { attached: false, staleCleanupCount: 1 };
                    }
                    this.renderLabelAsOverlay(label);
                    slot.add(label);
                }
                // Add the model to the slot
                return { attached: true };
            }

            this.cleanupOwnedModel(utils, appData, loadedMesh);
            return { attached: false };
        } catch (error) {
            this.cleanupOwnedModel(utils, appData, loadedMesh, rotationTarget || loadedMesh, [label, ...cleanupObjectsOnFailure]);
            console.error("Artwork3dModel: Error in handle3dmodelArtworkSlot", error);
            throw error;
        }
    }

    /**
     * Handles setup of diorama-type artwork in a slot
     * Includes label creation and material setup
     */
    static async handleSpaceArtworkSlot(loader: Loader, utils: Utils, appData: AppData, artworkData: ArtworkWrapper, model3D: any, imageDiorama: string, slotId: number, isLoadCurrent?: () => boolean) {
        const transientObjects: any[] = [];
        let label: any = null;

        try {
            const slot = utils.getSlotObjectBySlotTarget(slotId, appData)
            if (!slot) {
                this.cleanupOwnedModel(utils, appData, model3D);
                return { attached: false };
            }
            if (isLoadCurrent && !isLoadCurrent()) {
                this.cleanupOwnedModel(utils, appData, model3D);
                return { attached: false, staleCleanupCount: 1 };
            }

            utils.disposeOfChildren(slot, true)
            this.queueRender(appData);
            model3D.traverse((child: any) => {
                if (child.name == "cap") {
                    child.material = new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 0.01 })
                    appData.emissionModels.push(child)
                }
                child.userData = { ...slot.userData, artworkWrapper: artworkData } as Slot
            });
            model3D.userData = { ...slot.userData, artworkWrapper: artworkData } as Slot
            const yOffset = 0.01;
            const isMoMDiorama = this.isMoMDioramaSpace(appData, imageDiorama);
            const slotArtworkType = isMoMDiorama ? this.checkDioramaType(model3D) : imageDiorama;

            switch (slotArtworkType) {
                case "diorama":
                    // TO-DO remove the colider from the 3d models
                    const collider = model3D.getObjectByName("collider");
                    if (collider) {
                        collider.parent.remove(collider)
                    }
                    break;
                case "3dmodel":
                    // Compute bounding sphere and scale to fit within diameter of 0.45
                    const boundingSphere = isMoMDiorama
                        ? computeVisibleWorldBoundingSphere(model3D)
                        : computeWorldBoundingSphere(model3D);
                    if (boundingSphere && isFinite(boundingSphere.radius) && boundingSphere.radius > 0) {
                        const currentDiameter = boundingSphere.radius * 2;
                        const targetDiameter = isMoMDiorama ? 0.9 : 0.65;
                        if (isMoMDiorama || currentDiameter > targetDiameter) {
                            const scaleFactor = targetDiameter / currentDiameter;
                            model3D.scale.multiplyScalar(scaleFactor);
                        }
                    }

                    // After scaling, compute bounding box and position so lowest Y = 0
                    model3D.updateMatrixWorld(true);
                    const scaledBounds = isMoMDiorama
                        ? computeVisibleWorldBounds(model3D)
                        : computeWorldBounds(model3D);
                    if (scaledBounds && isFinite(scaledBounds.min.y)) {
                        model3D.position.y += -scaledBounds.min.y + yOffset;
                    }
                    const plane = createHexagon(0.83, "#777777");
                    const plane4 = createHexagon(0.83, "#ffffff", "#ffffff");
                    transientObjects.push(plane, plane4);
                    plane4.position.y = 1.39;
                    appData.emissionModels.push(plane4)
                    if (appData.spaceData.subtype != "pseudoMoM") {
                        slot.add(plane4);
                        slot.add(plane);
                    }
                    break;
                case "artwork":
                    const activeArtwork = artworkData.artworks[artworkData.activeArtwork]
                    const artworkModel = model3D.getObjectByName(activeArtwork.artworkData.meshName);
                    let artworkURL = activeArtwork.artworkData.dataUrl;
                    const { outputWidth, outputHeight } = await ArtworkMedia.loadNFTGraphics(loader, utils, artworkModel, artworkURL, artworkData);
                    if (isLoadCurrent && !isLoadCurrent()) {
                        this.cleanupOwnedModel(utils, appData, model3D, model3D, transientObjects);
                        return { attached: false, staleCleanupCount: 1 };
                    }

                    model3D.position.y += 0.05;
                    appData.lookAtCameraModels.push(model3D)
                    switch (activeArtwork.artworkData.frame.type) {
                        case "frame1":
                            ArtworkFrame.scaleFrame1(model3D, outputWidth, outputHeight, activeArtwork.artworkData.frame.thickness, activeArtwork.artworkData.frame.color)
                            break;
                        case "frame1_pprt":
                            ArtworkFrame.scaleFrame1_pprt(model3D, outputWidth, outputHeight, activeArtwork.artworkData.frame.thickness, activeArtwork.artworkData.frame.color, activeArtwork.artworkData.frame.pasPartoutSize)
                            break;
                    }

                    if (isMoMDiorama) {
                        const artworkBoundingSphere = computeVisibleWorldBoundingSphere(model3D);
                        if (artworkBoundingSphere && isFinite(artworkBoundingSphere.radius) && artworkBoundingSphere.radius > 0) {
                            const targetRadius = 0.45;
                            const scaleFactor = targetRadius / artworkBoundingSphere.radius;

                            model3D.scale.multiplyScalar(scaleFactor);

                            model3D.updateMatrixWorld(true);
                            const newBounds = computeVisibleWorldBounds(model3D);

                            if (newBounds && isFinite(newBounds.min.y)) {
                                model3D.position.y += -newBounds.min.y + yOffset;
                            }
                        }
                    } else {
                        const worldBounds = computeWorldBounds(model3D);

                        // Scale uniformly so height equals 0.45 and position so lowest point is at y=0
                        if (worldBounds && isFinite(worldBounds.min.y) && isFinite(worldBounds.max.y)) {
                            const currentHeight = worldBounds.max.y - worldBounds.min.y;
                            if (currentHeight > 0) {
                                const targetHeight = 0.45;
                                const scaleFactor = targetHeight / currentHeight;

                                // Apply uniform scaling
                                model3D.scale.multiplyScalar(scaleFactor);

                                // Update matrix and recompute bounds after scaling
                                model3D.updateMatrixWorld(true);
                                const newBounds = computeWorldBounds(model3D);

                                // Move object so its lowest point is at y=0
                                if (newBounds && isFinite(newBounds.min.y)) {
                                    model3D.position.y += -newBounds.min.y + yOffset;
                                }
                            }
                        }
                    }
                    const plane2 = createHexagon(0.83, "#777777");
                    const plane3 = createHexagon(0.83, "#ffffff", "#ffffff");
                    transientObjects.push(plane2, plane3);
                    plane3.position.y = 1.39;
                    appData.emissionModels.push(plane3)
                    if (appData.spaceData.subtype != "pseudoMoM") {
                        slot.add(plane3);
                        slot.add(plane2);
                    }
                    break;
            }

            const labelLayout = appData.spaceData?.subtype === "pseudoMoM" ? "pseudoMoMFixed" : "default";
            label = await ArtworkLabel.addArtworkLabel(loader, artworkData, {
                layout: labelLayout,
                showArrows: appData.spaceData?.subtype === "pseudoMoM"
                    ? this.shouldShowPseudoMoMArtworkArrows(appData, artworkData)
                    : undefined,
            });
            if (isLoadCurrent && !isLoadCurrent()) {
                this.cleanupOwnedModel(utils, appData, model3D, model3D, [label, ...transientObjects]);
                return { attached: false, staleCleanupCount: 1 };
            }
            slot.add(model3D)
            slot.add(label)
            this.queueRender(appData)
            return { attached: true };
        } catch (error) {
            this.cleanupOwnedModel(utils, appData, model3D, model3D, [label, ...transientObjects]);
            console.error("Artwork3dModel: Error in handleSpaceArtworkSlot", error);
            throw error;
        }
    }

}
